import Schema, { type RuleItem, type ValidateError } from 'async-validator'
import dayjs from 'dayjs'
import type { NamePath, RuleObject, RuleTrigger } from './types'

/**
 * 将字段路径参数统一归一化为数组结构
 */
export function normalizeNamePath(name?: NamePath): (string | number)[] {
  if (name === undefined || name === null || name === '') return []
  if (Array.isArray(name)) return name
  if (typeof name === 'number') return [name]
  return [name]
}

/**
 * 从对象中根据多层路径获取对应值
 */
export function getPathValue(obj: any, path: (string | number)[]): any {
  if (!obj || path.length === 0) return undefined
  let curr = obj
  for (const key of path) {
    if (curr === null || curr === undefined) return undefined
    curr = curr[key]
  }
  return curr
}

/**
 * 向对象的多层路径写入对应值
 */
export function setPathValue(obj: any, path: (string | number)[], value: any): void {
  if (!obj || path.length === 0) return
  if (path.some(key => ['__proto__', 'prototype', 'constructor'].includes(String(key)))) {
    throw new Error('表单字段路径不能包含原型属性')
  }
  let curr = obj
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (curr[key] === null || curr[key] === undefined || typeof curr[key] !== 'object') {
      curr[key] = typeof path[i + 1] === 'number' ? [] : {}
    }
    curr = curr[key]
  }
  curr[path[path.length - 1]] = value
}

/**
 * 深拷贝数据，用于 FormItem 的初始值保存与 resetFields 还原
 */
export function cloneDeepValue(val: any): any {
  if (val === null || typeof val !== 'object') return val
  if (val instanceof Date) return new Date(val.getTime())
  if (val instanceof RegExp) return new RegExp(val.source, val.flags)
  if (dayjs.isDayjs(val)) return val.clone()
  if (Array.isArray(val)) return val.map(cloneDeepValue)
  const copy: Record<string, any> = {}
  for (const key of Object.keys(val)) {
    copy[key] = cloneDeepValue(val[key])
  }
  return copy
}

/**
 * 判断规则定义的触发时机是否命中当前事件
 */
export function matchesTrigger(ruleTrigger?: RuleTrigger | RuleTrigger[], eventTrigger?: string): boolean {
  if (!eventTrigger) return true
  if (!ruleTrigger) return true
  if (Array.isArray(ruleTrigger)) {
    return ruleTrigger.includes(eventTrigger as RuleTrigger)
  }
  return ruleTrigger === eventTrigger
}

/** Run the same rule engine as the reference component, not a parallel partial implementation. */
export async function executeFieldValidation(
  value: any,
  rules: RuleObject[],
  fieldName: string,
  label?: string,
  trigger?: string,
  validateFirst = false
): Promise<string[]> {
  const matched = rules.filter(rule => matchesTrigger(rule.trigger, trigger))
  if (!matched.length) return []
  const schema = new Schema({ value: matched.map(rule => ({ ...rule, fullField: label || fieldName || '该字段' })) as RuleItem[] })
  try {
    await schema.validate({ value }, {
      first: validateFirst,
      suppressWarning: true,
      suppressValidatorError: true,
      messages: {
        default: '%s 校验失败', required: '%s为必填项', enum: '%s不在允许的选项中',
        whitespace: '%s不能仅包含空格',
        types: { string: '%s必须是文本', number: '%s必须是数字', boolean: '%s必须是布尔值', method: '%s必须是函数', regexp: '%s必须是正则表达式', integer: '%s必须是整数', float: '%s必须是浮点数', array: '%s必须是数组', object: '%s必须是对象', date: '%s必须是有效日期', url: '%s必须是有效链接', hex: '%s必须是十六进制颜色', email: '%s必须是有效邮箱' },
        string: { len: '%s长度必须为%s', min: '%s长度不能小于%s', max: '%s长度不能大于%s', range: '%s长度必须介于%s和%s之间' },
        number: { len: '%s必须等于%s', min: '%s不能小于%s', max: '%s不能大于%s', range: '%s必须介于%s和%s之间' },
        array: { len: '%s必须包含%s项', min: '%s至少包含%s项', max: '%s最多包含%s项', range: '%s必须包含%s至%s项' },
        pattern: { mismatch: '%s格式不匹配' },
        date: { format: '%s日期格式错误', parse: '%s不是有效日期', invalid: '%s不是有效日期' }
      }
    })
    return []
  } catch (error) {
    if (error && typeof error === 'object' && 'errors' in error && Array.isArray(error.errors)) {
      return (error.errors as ValidateError[]).map(item => String(item.message))
    }
    throw error
  }
}
